// src/mobile_pages/materials/MaterialsForm.jsx
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import MobileFormContentManager from "../../components/common/mobile/MobileFormContentManager";

import PROfficialForm from "./plan/Plan_new";
import POOfficialForm from "./order/Order_new";
import GROfficialForm from "./inout/In_new"; // GR
import GIOfficialForm from "./inout/Out_new"; // GI
import QCOfficialForm from "./check/Request_new"; // QC
import CKOfficialForm from "./check/Confirm_new"; // CK

const DOC_LABELS = {
  PS: "수급계획",
  PO: "발주서",
  GR: "입고서",
  GI: "출고서",
  QC: "검수 요청",
  CK: "검수 확인",
};

const OFFICIAL_FORMS = {
  PS: PROfficialForm,
  PO: POOfficialForm,
  GR: GROfficialForm,
  GI: GIOfficialForm,
  QC: QCOfficialForm,
  CK: CKOfficialForm,
};

const MATERIAL_DOC_DESCRIBE = {
  PS: "필요 자재의 규격·수량·단가를 입력해 수급 계획을 작성하세요.",
  PO: "자재 발주에 필요한 정보를 입력해 발주서를 작성하세요.",
  GR: "입고된 자재의 규격·수량을 확인하고 입고 내역을 기록하세요.",
  GI: "현장 사용 또는 반출 자재의 출고 정보를 입력하세요.",
  QC: "자재 검수가 필요한 경우 검수 항목과 사유를 입력하세요.",
  CK: "검수 결과를 확인하고 합격/불합격 여부를 기록하세요.",
};

export default function MaterialsForm() {
  const nav = useNavigate();
  const { id: projectId, context } = useParams();
  const location = useLocation();
  const [sp] = useSearchParams();

  const [summaryConfig, setSummaryConfig] = useState(null);
  const [ofcBody, setOfcBody] = useState(null);
  const [dns, setDns] = useState("");

  const intMode = 3;

  // URL 경로에서 form_type 추론 (ps/new, po/new 등)
  const pathFormTypeMap = {
    "ps": "PS",
    "po": "PO",
    "gr": "GR",
    "gi": "GI",
    "qc": "QC",
    "ck": "CK",
    "plan": "PS", // 하위 호환성
    "order": "PO", // 하위 호환성
    "inout": "GR", // 하위 호환성 (기본값)
    "check": "QC", // 하위 호환성 (기본값)
  };

  // URL 경로에서 form_type 추론
  const pathSegments = location.pathname.split('/');
  const materialsIndex = pathSegments.indexOf('materials');
  const pathSegment = materialsIndex >= 0 && pathSegments.length > materialsIndex + 1
    ? pathSegments[materialsIndex + 1]
    : null;

  const pathFormType = pathSegment ? pathFormTypeMap[pathSegment.toLowerCase()] : null;
  const contextFormType = context ? pathFormTypeMap[context.toLowerCase()] : null;
  const queryFormType = sp.get("form_type");

  const rawFormType = (pathFormType || contextFormType || queryFormType || "PS").toUpperCase();
  const formType =
    ["PS", "PO", "GR", "GI", "QC", "CK"].includes(rawFormType)
      ? rawFormType
      : "PS";

  const stage = (sp.get("stage") || "").toUpperCase();
  const docLabel = DOC_LABELS[formType] || formType;

  const title = useMemo(() => {
    const sub = stage ? ` · ${stage}` : "";
    return `${docLabel}`;
  }, [docLabel, stage]);

  // 백엔드 컬럼 매핑
  const columnOverrides = useMemo(() => {
    switch (formType) {
      case "GR":
      case "GI":
        return { qty: "qty", unit_price: "price", amount: "total" };

      case "PO":
        return { qty: "qty", unit_price: "price", amount: "amount" };

      case "QC":
      case "CK":
        return { qty: "qty", ok_qty: "ok_qty", ng_qty: "ng_qty" };

      case "PS":
      default:
        return {};
    }
  }, [formType]);

  // FormContentManager body 구동
  const getBody = useCallback(() => {
    return {
      layout: null,
      values: ofcBody || {},
      column_overrides: columnOverrides,
    };
  }, [ofcBody, columnOverrides]);

  const BOARD_URL = `/project/${projectId}/materials/materialboard`;
  const OfcForm = OFFICIAL_FORMS[formType];

  // 🔥 요약 생성 (모든 FormType 공통)
  useEffect(() => {
    if (!ofcBody) return;

    const f = ofcBody;

    const baseSections = [
      { label: "문서명", value: f.title || "-" },
      {
        label: "작성일",
        value:
          f.po_date ||
          f.issue_date ||
          f.date ||
          "-",
      },
    ];

    if (f.supplier)
      baseSections.push({
        label: "공급업체",
        value: f.supplier,
      });
    if (f.to_site)
      baseSections.push({
        label: "출고처/사용처",
        value: f.to_site,
      });
    if (f.delivery_place)
      baseSections.push({
        label: "납품장소",
        value: f.delivery_place,
      });

    if (typeof f.total === "number") {
      baseSections.push({
        label: "총액",
        value: f.total.toLocaleString(),
      });
    }

    const items = f.items || [];
    const itemList = items.slice(0, 5).map((i) => {
      const price = i.price?.toLocaleString?.() || "-";
      const amount = i.amount?.toLocaleString?.() || i.total || "-";
      return `${i.name || "(품명)"} × ${i.qty} @ ${price} = ${amount}`;
    });

    if (items.length > 5)
      itemList.push(`기타 ${items.length - 5}행…`);

    const config = {
      title: `${docLabel} 요약`,
      sections: baseSections,
      list: [
        {
          label: "품목 요약",
          items: itemList,
        },
      ],
      footerNote: `총 ${items.length}개 항목`,
    };

    setSummaryConfig(config);
  }, [ofcBody, docLabel]);

  return (
    <div
      className="mobile-page-root mb-16"
      style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
    >
      <div
        className="mobile-page-root-inner space-y-3"
        style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
      >
        {/* MobileFormContentManager (요약 포함) */}
        <MobileFormContentManager
          params={{
            module: "material",
            formType,
            mode: intMode,
            scope: "project",
            project: projectId,
            dns,
          }}
          getBody={getBody}
          docName={title}
          onChangeId={(newDns) => setDns(newDns)}
          redirectTo={BOARD_URL}
          summaryComponent={ofcBody?.summary_component}
          docDescribe={MATERIAL_DOC_DESCRIBE[formType] || `${title}을 작성하세요.`}
        >
          {OfcForm ? (
            <OfcForm
              projectId={projectId}
              formType={formType}
              docLabel={docLabel}
              initial={{ subtype: stage }}
              readOnly={false}
              onChange={setOfcBody} // ⭐ 공식폼에서 데이터만 받아옴
              currentDns={dns}
            />
          ) : (
            <div className="border p-4 rounded bg-amber-50 text-amber-700">
              지원되지 않는 공식 폼입니다: <b>{formType}</b>
            </div>
          )}
        </MobileFormContentManager>
      </div>
    </div>
  );
}
